import styled, { css } from 'styled-components';
import { Icon } from '@/app/common/icons/Icon';
import { PillRow, Separator } from '@/app/common/filters/surfaces/styled.ts';
import { evoDropdownCard, evoDropdownOption, focusRing } from '@/app/components/search/styles/mixins.ts';

export const Container = styled.div<{ disabled?: boolean }>`
  width: 100%;
  position: relative;
  background-color: white;

  ${({ disabled }) =>
    disabled &&
    css`
      pointer-events: none;
      opacity: 0.5;
    `}
`;

// The evo design's search field: 52px, a 20px icon 16px in and the text 8px past it.
const EVO_FIELD_HEIGHT = 52;
const EVO_TEXT_INSET = 44;
const EVO_LABEL_TOP = 5;
const EVO_DROPDOWN_GAP = 4;

export const InputWrapper = styled.div<{ $evo?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: ${({ $evo }) => ($evo ? EVO_FIELD_HEIGHT : 56)}px;
  padding: 0 16px 0 ${({ $evo }) => ($evo ? EVO_TEXT_INSET : 40)}px;
  border-radius: 8px;
  background-color: ${({ theme, $evo }) =>
    $evo ? theme.kallisto.color.background.static.backgroundLayer01 : theme.kallisto.color.common.black[50]};
  box-sizing: border-box;
  cursor: text;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.kallisto.color.border.interactive.borderInteractivePrimary};
  }

  ${({ $evo, theme }) =>
    $evo &&
    css`
      &:focus-within {
        box-shadow: inset 0 0 0 2px ${theme.commons.colors.gray800};
      }
    `}
`;

export const SearchIcon = styled(Icon)<{ $evo?: boolean }>`
  position: absolute;
  left: ${({ $evo }) => ($evo ? '16px' : '10.5px')};
  top: 50%;
  transform: translateY(-50%);
  flex-shrink: 0;
  pointer-events: none;
`;

export const InputLabel = styled.label<{ hasValue: boolean; isFocused: boolean; $evo?: boolean }>`
  position: absolute;
  left: ${({ $evo }) => ($evo ? EVO_TEXT_INSET : 40)}px;
  top: 50%;
  transform: translateY(-50%);
  font-size: ${({ hasValue, isFocused }) => (hasValue || isFocused ? '11px' : '16px')};
  color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
  pointer-events: none;
  transition:
    font-size 0.15s ease,
    top 0.15s ease;

  ${({ hasValue, isFocused }) =>
    (hasValue || isFocused) &&
    css`
      top: 10px;
      transform: none;
    `}

  ${({ $evo, hasValue, isFocused, theme }) =>
    $evo &&
    css`
      ${theme.kallisto.mixins.font(hasValue || isFocused ? 'Caption' : 'Body')};
      ${(hasValue || isFocused) && `top: ${EVO_LABEL_TOP}px;`}

      ${InputWrapper}:focus-within & {
        ${theme.kallisto.mixins.font('Caption')};
        top: ${EVO_LABEL_TOP}px;
        transform: none;
      }
    `}
`;

export const StyledInput = styled.input<{ $evo?: boolean }>`
  width: 100%;
  height: 20px;
  margin-top: 24px;
  border: none;
  outline: none;
  background: transparent;
  font-size: 16px;
  line-height: 20px;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  padding: 0;
  cursor: text;

  &:disabled {
    cursor: not-allowed;
  }

  ${({ $evo, theme }) =>
    $evo &&
    css`
      height: 24px;
      margin-top: 18px;
      ${theme.kallisto.mixins.font('Body')};
    `}
`;

export const DropdownOverlay = styled.div<{ $evo?: boolean }>`
  position: absolute;
  top: ${({ $evo }) => ($evo ? EVO_FIELD_HEIGHT + EVO_DROPDOWN_GAP : 56)}px;
  left: 0;
  width: 100%;
  border-radius: 8px;
  box-shadow:
    0 24px 32px rgba(0, 0, 0, 0.04),
    0 16px 24px rgba(0, 0, 0, 0.04),
    0 4px 8px rgba(0, 0, 0, 0.04),
    0 0 10px rgba(0, 0, 0, 0.04);
  max-height: 240px;
  overflow-y: auto;
  overscroll-behavior: none;
  z-index: 10;
  background-color: white;

  &::-webkit-scrollbar-track,
  &::-webkit-scrollbar {
    width: 3px;
    background-color: ${({ theme }) => theme.kallisto.color.common.black[50]};
  }

  &::-webkit-scrollbar-thumb {
    background-color: #080808;
  }

  ${({ $evo }) =>
    $evo &&
    css`
      ${evoDropdownCard};

      &[data-side='top'] {
        top: auto;
        bottom: calc(100% + ${EVO_DROPDOWN_GAP}px);
      }
    `}
`;

export const OptionItem = styled.li<{
  isSelected: boolean;
  isHighlighted: boolean;
  isDisabled: boolean;
  $evo?: boolean;
}>`
  list-style: none;
  padding: 8px 16px;
  min-height: 48px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  background-color: transparent;

  ${({ $evo }) =>
    $evo &&
    css`
      min-height: 0;
      ${evoDropdownOption};
    `}

  ${({ isHighlighted, $evo, theme }) =>
    isHighlighted &&
    ($evo
      ? focusRing
      : css`
          background-color: ${theme.kallisto.color.background.interactive.bgInteractiveSecondary};
        `)}

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      font-weight: 600;
      color: ${theme.kallisto.color.text.interactive.textInteractivePrimary};
    `}

  ${({ isDisabled }) =>
    isDisabled &&
    css`
      opacity: 0.4;
      cursor: not-allowed;
    `}

  ${({ $evo, theme }) =>
    !$evo &&
    css`
      &:hover {
        background-color: ${theme.kallisto.color.background.interactive.bgInteractiveSecondary};
      }
    `}
`;

export const OptionDivider = styled(Separator)`
  width: auto;
  margin: ${({ theme }) => `${theme.kallisto.space.space50} ${theme.kallisto.space.space100}`};
`;

export const SelectedChips = styled(PillRow)`
  margin-top: ${({ theme }) => theme.kallisto.space.space100};
`;

export const SelectedOptionItem = styled.li`
  list-style: none;
  min-width: 0;
  display: flex;
`;

export const NoResults = styled.div`
  padding: 8px 16px;
  font-size: 14px;
  color: ${({ theme }) => theme.kallisto.color.common.semantic.error[500]};
`;
